/**
 * PWA Install Prompt
 * Shows a banner on /pa pages when the app is not installed.
 * - Android: uses beforeinstallprompt event
 * - iOS: shows manual instructions (Add to Home Screen)
 */

import { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as any).standalone === true;
}

export function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isApple, setIsApple] = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (isStandalone()) return;

    // Don't show if dismissed recently (24h)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 24 * 60 * 60 * 1000) return;

    if (isIOS()) {
      setIsApple(true);
      setShowPrompt(true);
      return;
    }

    // Android/Chrome: listen for beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', String(Date.now()));
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-[140px] left-4 right-4 z-[55] max-w-lg mx-auto animate-[slideUp_0.3s_ease-out]">
      <div className="bg-[#1a1040] border border-[#5048e5]/30 rounded-2xl p-4 shadow-[0_0_30px_rgba(80,72,229,0.2)]">
        <button onClick={handleDismiss} className="absolute top-3 right-3 text-zinc-600 hover:text-zinc-400">
          <X className="h-4 w-4" />
        </button>

        {isApple ? (
          // iOS instructions
          <div className="flex items-start gap-3 pr-6">
            <div className="w-10 h-10 rounded-xl bg-[#5048e5]/20 flex items-center justify-center shrink-0">
              <Share className="h-5 w-5 text-[#7c75f0]" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">Install RoniDoc PA</p>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                Tap <Share className="h-3 w-3 inline text-[#7c75f0]" /> in your browser, then <strong>"Add to Home Screen"</strong> for the full app experience.
              </p>
            </div>
          </div>
        ) : (
          // Android/Chrome install button
          <div className="flex items-center gap-3 pr-6">
            <div className="w-10 h-10 rounded-xl bg-[#5048e5]/20 flex items-center justify-center shrink-0">
              <Download className="h-5 w-5 text-[#7c75f0]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-white">Install RoniDoc PA</p>
              <p className="text-xs text-zinc-400 mt-0.5">Add to your home screen for quick access</p>
            </div>
            <button
              onClick={handleInstall}
              className="px-4 py-2 bg-[#5048e5] hover:bg-[#5f58e8] text-white text-xs font-medium rounded-full transition shrink-0"
            >
              Install
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
